import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import apiClient from '../../../../../api/axios';
import type {
  TMenuTranslationResponse,
  TCreateMenuTranslationPayload,
} from '../schemas';

const KEYS = {
  menuTranslation: (restaurantId: string, menuItemId: string, lang: string) =>
    ['menu-translation', restaurantId, menuItemId, lang] as const,
};

export const useGetMenuTranslation = (
  restaurantId: string,
  menuItemId: string,
  languageCode: string,
  enabled = true
) =>
  useQuery({
    queryKey: KEYS.menuTranslation(restaurantId, menuItemId, languageCode),
    queryFn: async () => {
      const response = await apiClient.get<TMenuTranslationResponse>(
        `translation/menu/${menuItemId}/translations?lang=${languageCode}`,
        { headers: { 'Restaurant-ID': restaurantId } }
      );
      const rows = response.data?.data?.rows ?? [];
      return rows.length > 0 ? rows[0] : null;
    },
    enabled: enabled && !!restaurantId && !!menuItemId && !!languageCode,
    staleTime: 60_000,
  });

export const useCreateMenuTranslation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      restaurantId,
      menuItemId,
      translationData,
    }: TCreateMenuTranslationPayload) => {
      const response = await apiClient.post<TMenuTranslationResponse>(
        `translation/menu/${menuItemId}`,
        translationData,
        { headers: { 'Restaurant-ID': restaurantId } }
      );
      if (!response.data?.success) throw new Error(response.data?.msg ?? 'Unknown error');
      return response.data;
    },
    onSuccess: (_data, { restaurantId, menuItemId, translationData }) => {
      queryClient.invalidateQueries({
        queryKey: KEYS.menuTranslation(restaurantId, menuItemId, translationData.lang),
      });
    },
  });
};
